// Central adaptation decision engine. Phase F2.
//
// Spec §"Full adaptation decision engine" (lines 1453–1812). This is
// the composition module: every prior phase (C / D / E / F1 / F3)
// converges here. The engine expresses priority order DIRECTLY — it
// never silently falls through from safety / support leakage / weak
// evidence into advancement. Exactly one priority fires per call.
//
// Priority order (spec lines 1804–1812):
//   1. safety/content invalid       → review (safe fallback)
//   2. same-error loop              → walkback OR remediate
//   3. high frustration             → review
//   4. support-mode leakage         → review (lower-support retest)
//   5. missing transfer             → review (transfer task)
//   6. missing delayed review       → review (delayed review)
//   7. mastery + next skill too early → enrich
//   8. mastery + all gates passed   → advance OR compactReview
//   9. partial progress             → review
//  10. weak evidence (fallthrough)  → walkback OR remediate
//
// Composition imports:
//   - Phase C    requiresRetestWithoutSupport  (priority 4)
//   - Phase E    selectWalkbackSkill           (priority 2)
//   - Phase E    HINT_ORDER                    (summarizeSupportMode)
//   - Phase F1   evaluateAccelerationEligibility (priority 8)
//   - Phase F3   buildParentExplanation        (finalizeDecision)
//
// §F2-Q1 (Phase F4 resolution): evidenceUsed is populated by
// `buildEvidenceUsed` from the engine inputs — attempt itemIds from
// attemptWindow.relevantAttempts, transferTaskIds from
// transferEvidence.taskIdsTried, delayedReviewAttemptIds from
// delayedReviewEvidence.reviewAttempts, misconceptionId from
// misconceptionSignal. Computed once at decision-engine entry; the
// payload is identical regardless of which priority branch fires
// (it describes the WHOLE evidence pool considered, not just the
// evidence the chosen branch happened to read).
//
// §F2-Q2: HINT_ORDER is imported from misconceptionRouting (Phase E's
// local copy, now exported) instead of adding a 4th private copy.
// FOLLOWUPS.md tracks the Phase G consolidation to constants.ts.

import type {
  AdaptationDecision,
  AdaptationDecisionInput,
  AdaptationDecisionType,
  AttemptWindow,
  DecisionBlockerCode,
  DecisionReasonCode,
  DelayedReviewEvidence,
  MisconceptionSignal,
  ParentExplanationPayload,
  RequiredNextEvidence,
  TransferEvidence,
} from "./types";
import {
  requiresRetestWithoutSupport,
  hasPictureSupportEvidence,
  hasPostIdkCorrectEvidence,
  readAloudIsSupportForChannel,
} from "./supportModeGating";
import { selectWalkbackSkill, HINT_ORDER } from "./misconceptionRouting";
import { evaluateAccelerationEligibility } from "./accelerationEngine";
import { inferChannel } from "./masteryClassInference";
import { buildParentExplanation, hasSameDayEvidence } from "./parentExplanation";

// ── Public surface ───────────────────────────────────────────────────

/** Central decision engine. Walks the 10-priority chain in order and
 * returns exactly one AdaptationDecision. Per spec lines 1487–1812. */
export function decideNextActivity(
  input: AdaptationDecisionInput,
): AdaptationDecision {
  const {
    childId,
    currentSkillId,
    attemptWindow,
    masteryState,
    transferEvidence,
    delayedReviewEvidence,
    misconceptionSignal,
    progressionState,
    currentContentSafety,
    candidateNextContentSafety,
  } = input;

  const debugTrace: string[] = [
    `skill=${currentSkillId}`,
    `accuracy=${masteryState.accuracyRate.toFixed(2)}`,
    `independence=${masteryState.independentRate.toFixed(2)}`,
    `transferPassed=${transferEvidence.passed}`,
    `delayedReviewPassed=${delayedReviewEvidence.passed}`,
    `sameErrorLoop=${misconceptionSignal.sameErrorLoop}`,
    `contentLaunchStatus=${currentContentSafety.launchStatus}`,
  ];

  // Phase F4: populate evidenceUsed once from inputs (§F2-Q1 deferred
  // resolution). Threaded through PartialDecision into finalizeDecision.
  const evidenceUsed = buildEvidenceUsed(
    attemptWindow,
    transferEvidence,
    delayedReviewEvidence,
    misconceptionSignal,
  );

  // MA-338: same-day check runs over the identical attempt pool
  // `evidenceUsed.attempts` was stripped from, so the receipt's "Today"
  // claim is provably about the same evidence disclosed to the parent.
  const hasSameDayAttempt = hasSameDayEvidence(attemptWindow.relevantAttempts);
  // ⭐MA-802 — a read-aloud that is NOT support on this channel (listening, oral vocab,
  // grammar, handwriting, maths) is still a fact the parent should be told, honestly
  // ("listened to the text read aloud"), not hidden behind "worked independently".
  const readAloudHeard = readAloudHeardOnNonSupportChannel(attemptWindow);
  // ⭐MA-816: copy-only (like readAloudHeard) — lets the scaffolded opener keep the read-aloud disclosure ONLY for the post-IDK case.
  const postIdkHelped = hasPostIdkCorrectEvidence(attemptWindow);

  // ── 1. safety/content invalid → review with safe fallback ────────
  if (
    !currentContentSafety.rawChildFacingLLMBlocked ||
    currentContentSafety.launchStatus === "exclude_independent_practice" ||
    currentContentSafety.launchStatus === "expert_review_needed" ||
    currentContentSafety.expertReviewNeeded ||
    !currentContentSafety.validatorPassed
  ) {
    const reasonCodes: DecisionReasonCode[] = [];
    const blockerCodes: DecisionBlockerCode[] = [];

    blockerCodes.push(
      currentContentSafety.launchStatus === "exclude_independent_practice"
        ? "BLOCK_CONTENT_NOT_LAUNCH_SAFE"
        : "BLOCK_EXPERT_REVIEW_NEEDED",
    );
    if (!currentContentSafety.rawChildFacingLLMBlocked) {
      blockerCodes.push("BLOCK_RAW_RUNTIME_LLM");
    }
    reasonCodes.push(
      currentContentSafety.expertReviewNeeded
        ? "CONTENT_EXPERT_REVIEW_NEEDED"
        : "CONTENT_EXCLUDED_FROM_INDEPENDENT_PRACTICE",
    );

    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "assessmentProbe",
      selectedContentFile: currentContentSafety.contentFile,
      selectedContentId: currentContentSafety.contentId,
      reasonCodes,
      blockerCodes,
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: ["safe_content_swap"],
      debugTrace: debugTrace.concat("priority=1:safety_fallback"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 2. same-error loop → remediate or walkback ───────────────────
  if (misconceptionSignal.sameErrorLoop) {
    const reasonCodes: DecisionReasonCode[] = ["SAME_ERROR_LOOP_DETECTED"];
    const blockerCodes: DecisionBlockerCode[] = ["BLOCK_SAME_ERROR_LOOP"];

    const walkbackSkillId = selectWalkbackSkill(
      currentSkillId,
      misconceptionSignal.misconceptionId,
      progressionState,
    );
    const useWalkback = Boolean(
      walkbackSkillId && progressionState.walkbackSkillIds.length > 0,
    );

    reasonCodes.push(useWalkback ? "NEEDS_WALKBACK" : "NEEDS_REMEDIATION");

    return finalizeDecision(useWalkback ? "walkback" : "remediate", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: useWalkback ? walkbackSkillId : currentSkillId,
      selectedActivityType: useWalkback ? "walkbackPractice" : "remediationContrast",
      selectedContentFile: "/src/curriculum/manifests/misconceptionInterventionMap.json",
      selectedContentId: misconceptionSignal.interventionId,
      reasonCodes,
      blockerCodes,
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: [
        useWalkback ? "walkback_prerequisite" : "misconception_remediation",
      ],
      debugTrace: debugTrace.concat(
        `priority=2:${useWalkback ? "walkback" : "remediate"}`,
      ),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 3. high frustration → review with easier safe support ────────
  if (attemptWindow.frustrationRate >= 0.34) {
    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "directPractice",
      selectedContentFile: currentContentSafety.contentFile,
      selectedContentId: null,
      reasonCodes: ["HIGH_FRUSTRATION_DETECTED"],
      blockerCodes: [],
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: ["independent_retest"],
      debugTrace: debugTrace.concat("priority=3:frustration_review"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 4. support-mode leakage → retest without support ─────────────
  if (requiresRetestWithoutSupport(attemptWindow)) {
    const reasonCodes: DecisionReasonCode[] = ["SUPPORT_RETEST_REQUIRED"];
    const blockerCodes: DecisionBlockerCode[] = [];

    if (attemptWindow.highHintRate > 0.4) {
      reasonCodes.push("HIGH_HINT_USAGE");
      blockerCodes.push("BLOCK_HIGH_HINT_USE");
    }
    if (attemptWindow.h4Used) {
      reasonCodes.push("H4_USED");
      blockerCodes.push("BLOCK_H4_SUCCESS");
    }
    if (attemptWindow.readAloudOnlyReadingEvidence) {
      reasonCodes.push("AUDIO_SUPPORTED_ONLY");
      blockerCodes.push("BLOCK_AUDIO_SUPPORTED_READING");
    }
    if (attemptWindow.oralOnlyReadingEvidence) {
      reasonCodes.push("ORAL_ONLY_EVIDENCE");
      blockerCodes.push("BLOCK_ORAL_VOCAB_FOR_READING");
    }
    // MA-485 slice b: no existing limb above names WHY the retest fired
    // when the cause is a picture/post-IDK support that visually named
    // the answer — that case fell through to parentExplanation's generic
    // "success was seen with support" phrasing. Additive reason code only;
    // does not change which branch fires or the retest gating itself.
    // ⭐MA-816: split. The picture sentence needs a picture/sound box that
    // was actually shown on a correct attempt; a post-IDK correct on an item
    // with no such support (listening, supports ["SEN"]) gets POST_IDK_RETEST
    // and its own true sentence. SUPPORT_RETEST_REQUIRED / blockers /
    // priority are untouched, so acceleration behaves exactly as before.
    if (hasPictureSupportEvidence(attemptWindow)) {
      reasonCodes.push("PICTURE_SUPPORT_RETEST");
    } else if (hasPostIdkCorrectEvidence(attemptWindow)) {
      reasonCodes.push("POST_IDK_RETEST");
    }

    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "assessmentProbe",
      selectedContentFile: currentContentSafety.contentFile,
      selectedContentId: null,
      reasonCodes,
      blockerCodes,
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: ["independent_retest"],
      debugTrace: debugTrace.concat("priority=4:support_retest"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 5. missing REQUIRED transfer → assign transfer task as review ──
  // MA-295: was a bare `!transferEvidence.passed`, which — with `required`
  // hardcoded true and no live serve/TAG path anywhere — fired for EVERY
  // skill with history: priorities 6-10 (delayed-review / enrich / advance /
  // compactReview) were unreachable dead code and every mission was labeled
  // "review_due". Transfer now gates only where it is actually required
  // (a live serve+TAG path exists for the skill — transferServingPathExists;
  // registry empty until MA-303 lands skills with their serving path).
  if (transferEvidence.required && !transferEvidence.passed) {
    const reasonCodes: DecisionReasonCode[] = [
      transferEvidence.requiresConnectedText
        ? "CONNECTED_TEXT_REQUIRED"
        : transferEvidence.requiresWordProblem
          ? "WORD_PROBLEM_TRANSFER_REQUIRED"
          : "TRANSFER_REQUIRED",
    ];
    const blockerCodes: DecisionBlockerCode[] = ["BLOCK_NO_TRANSFER"];

    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: transferEvidence.requiresConnectedText
        ? "connectedText"
        : transferEvidence.requiresWordProblem
          ? "wordProblem"
          : "transferTask",
      selectedContentFile: "/src/curriculum/manifests/transferTasks.json",
      selectedContentId: null,
      reasonCodes,
      blockerCodes,
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: [
        transferEvidence.requiresConnectedText
          ? "connected_text_transfer"
          : transferEvidence.requiresWordProblem
            ? "word_problem_transfer"
            : "transfer_task",
      ],
      debugTrace: debugTrace.concat("priority=5:transfer_gate"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 6. missing delayed review → schedule delayed review ──────────
  if (!delayedReviewEvidence.passed) {
    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "delayedReview",
      selectedContentFile: currentContentSafety.contentFile,
      selectedContentId: null,
      reasonCodes: ["DELAYED_REVIEW_REQUIRED"],
      blockerCodes: ["BLOCK_NO_DELAYED_REVIEW"],
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: ["delayed_review"],
      debugTrace: debugTrace.concat("priority=6:delayed_review_gate"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 7. mastery but next skill too early → enrich ─────────────────
  if (progressionState.nextFormalSkillTooEarly) {
    return finalizeDecision("enrich", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "enrichmentChallenge",
      selectedContentFile: "/src/curriculum/manifests/accelerationRules.json",
      selectedContentId: null,
      reasonCodes: ["AGE_BAND_ENRICH_INSTEAD"],
      blockerCodes: ["BLOCK_NEXT_SKILL_TOO_EARLY"],
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: ["enrichment_instead"],
      debugTrace: debugTrace.concat("priority=7:enrich_instead"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 8. mastery + all gates passed → advance or compactReview ─────
  if (masteryState.durableMastery && progressionState.nextFormalSkillId) {
    const acceleration = evaluateAccelerationEligibility(
      currentSkillId,
      masteryState,
      attemptWindow,
      progressionState,
      candidateNextContentSafety,
    );

    if (acceleration.action === "compact_review") {
      return finalizeDecision("compactReview", {
        childId,
        currentSkillId,
        nextSkillId: progressionState.nextFormalSkillId,
        remediationSkillId: null,
        selectedActivityType: "assessmentProbe",
        selectedContentFile: "/src/curriculum/manifests/accelerationRules.json",
        selectedContentId: null,
        reasonCodes: acceleration.reasonCodes,
        blockerCodes: acceleration.blockerCodes,
        supportModeSummary: summarizeSupportMode(attemptWindow),
        requiredFollowUp: [],
        debugTrace: debugTrace.concat("priority=8:compact_review"),
        evidenceUsed,
        hasSameDayAttempt,
        readAloudHeard,
        postIdkHelped,
        durableMastery: masteryState.durableMastery,
      });
    }

    return finalizeDecision("advance", {
      childId,
      currentSkillId,
      nextSkillId: progressionState.nextFormalSkillId,
      remediationSkillId: null,
      selectedActivityType: "directPractice",
      selectedContentFile:
        candidateNextContentSafety?.contentFile ??
        currentContentSafety.contentFile,
      selectedContentId: candidateNextContentSafety?.contentId ?? null,
      reasonCodes: acceleration.reasonCodes.concat(["READY_FOR_ADVANCEMENT"]),
      blockerCodes: acceleration.blockerCodes,
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: [],
      debugTrace: debugTrace.concat("priority=8:advance"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 9. partial progress → review ─────────────────────────────────
  if (masteryState.provisionalMastery || masteryState.readinessOnly) {
    return finalizeDecision("review", {
      childId,
      currentSkillId,
      nextSkillId: null,
      remediationSkillId: null,
      selectedActivityType: "directPractice",
      selectedContentFile: currentContentSafety.contentFile,
      selectedContentId: null,
      reasonCodes: ["PARTIAL_PROGRESS_OBSERVED"],
      blockerCodes: [],
      supportModeSummary: summarizeSupportMode(attemptWindow),
      requiredFollowUp: masteryState.requiredNextEvidence,
      debugTrace: debugTrace.concat("priority=9:review"),
      evidenceUsed,
      hasSameDayAttempt,
      readAloudHeard,
      postIdkHelped,
      durableMastery: masteryState.durableMastery,
    });
  }

  // ── 10. weak evidence → remediate or walkback ────────────────────
  const weakUseWalkback = progressionState.walkbackSkillIds.length > 0;
  return finalizeDecision(weakUseWalkback ? "walkback" : "remediate", {
    childId,
    currentSkillId,
    nextSkillId: null,
    remediationSkillId: weakUseWalkback
      ? progressionState.walkbackSkillIds[0]
      : currentSkillId,
    selectedActivityType: weakUseWalkback
      ? "walkbackPractice"
      : "remediationContrast",
    selectedContentFile: weakUseWalkback
      ? "/src/curriculum/manifests/learningProgressions.json"
      : "/src/curriculum/manifests/misconceptionInterventionMap.json",
    selectedContentId: null,
    reasonCodes: [weakUseWalkback ? "NEEDS_WALKBACK" : "NEEDS_REMEDIATION"],
    blockerCodes: [],
    supportModeSummary: summarizeSupportMode(attemptWindow),
    requiredFollowUp: [
      weakUseWalkback ? "walkback_prerequisite" : "misconception_remediation",
    ],
    debugTrace: debugTrace.concat(
      `priority=10:${weakUseWalkback ? "walkback" : "remediate"}`,
    ),
    evidenceUsed,
    hasSameDayAttempt,
    readAloudHeard,
    postIdkHelped,
    durableMastery: masteryState.durableMastery,
  });
}

// ── Internal helpers ─────────────────────────────────────────────────

/** Translate AttemptWindow into the parent-payload supportLevelUsed
 * enum. Priority chain per spec lines 1740–1751: adult > mascot >
 * audio > visual > scaffolded > independent. The first match wins. */
export function summarizeSupportMode(
  attemptWindow: AttemptWindow,
): ParentExplanationPayload["supportLevelUsed"] {
  if (attemptWindow.relevantAttempts.some((a) => a.adultSupportUsed)) {
    return "adult-supported";
  }
  if (attemptWindow.relevantAttempts.some((a) => a.mascotSupportUsed)) {
    return "mascot-guided";
  }
  // ⭐MA-802 — channel-scoped by the SAME predicate MA-786 put on the window flag
  // (readAloudIsSupportForChannel): a read-aloud is "support" only on the reading
  // channels. Channel-blind, every ms.read.18/19/20 child who used the passage
  // "Read it to me" (the intended path) got "They used read-aloud support to access
  // the words." and successQualification "supported" on the parent card.
  // Whole-practice sentence (MA-802 RED round 2): ANY read-aloud in the window is reported,
  // correct or not. The hold-back gates (retest / readAloudOnlyReadingEvidence) stay correct-only.
  if (
    readAloudIsSupportForChannel(inferChannel(attemptWindow.skillId)) &&
    attemptWindow.relevantAttempts.some((a) => a.readAloudUsed)
  ) {
    return "audio-supported";
  }
  if (
    attemptWindow.relevantAttempts.some(
      (a) =>
        a.pictureSupportUsed ||
        a.supportMode.includes("visualScaffolded") ||
        a.supportMode.includes("soundBoxSupported"),
    )
  ) {
    return "visual-supported";
  }
  // ⭐MA-816: a correct answer that followed an "I don't know" press (which
  // brought up a hint, MA-483 rung) is a scaffolded success, not an independent
  // one. Window-wide, so it also applies to the priority 1-3 branches (copy only).
  // Ranks below picture/audio so every other window keeps its opener.
  if (hasPostIdkCorrectEvidence(attemptWindow)) {
    return "scaffolded";
  }
  if (
    attemptWindow.relevantAttempts.some(
      (a) => HINT_ORDER[a.hintLevel] > HINT_ORDER.H1,
    )
  ) {
    return "scaffolded";
  }
  return "independent";
}

/** ⭐MA-802 — true when ANY attempt in the window used read-aloud on a channel where
 * read-aloud is not support (so supportLevelUsed stays "independent" but the copy discloses it). */
export function readAloudHeardOnNonSupportChannel(attemptWindow: AttemptWindow): boolean {
  return (
    !readAloudIsSupportForChannel(inferChannel(attemptWindow.skillId)) &&
    attemptWindow.relevantAttempts.some((a) => a.readAloudUsed)
  );
}

interface PartialDecision {
  childId: string;
  currentSkillId: string;
  nextSkillId: string | null;
  remediationSkillId: string | null;
  selectedActivityType: AdaptationDecision["selectedActivityType"];
  selectedContentFile: string;
  selectedContentId: string | null;
  reasonCodes: DecisionReasonCode[];
  blockerCodes: DecisionBlockerCode[];
  supportModeSummary: ParentExplanationPayload["supportLevelUsed"];
  requiredFollowUp: RequiredNextEvidence[];
  debugTrace: string[];
  evidenceUsed: AdaptationDecision["evidenceUsed"];
  // MA-338: threaded into buildParentExplanation only — never surfaced on
  // the public AdaptationDecision type (copy-layer input, not engine state).
  hasSameDayAttempt: boolean;
  // MA-802: copy-only, like hasSameDayAttempt. Never surfaced on AdaptationDecision.
  readAloudHeard: boolean;
  // MA-816: copy-only; see the const in decideNextActivity.
  postIdkHelped: boolean;
  // MA-630 §4e(b): threaded into buildParentExplanation only, same
  // discipline as hasSameDayAttempt above — never surfaced on the public
  // AdaptationDecision type. Copy-only signal so inferStrength can avoid
  // calling a durably-mastered skill "partial progress" when it lands on
  // priority 9 with unlocks: [] (nextFormalSkillId: null skips priority 8).
  durableMastery: boolean;
}

/** Build the evidenceUsed payload from engine inputs. Phase F4
 * resolves the §F2-Q1 deferral: every decision discloses the WHOLE
 * evidence pool considered (attempts, transfer tasks, delayed-review
 * attempts, dominant misconception), not just the evidence the
 * chosen priority branch happened to read. This shape is what
 * downstream dashboards and parent UI consume to render
 * "decision based on N attempts" surfaces.
 *
 * - `attempts`           — itemIds from attemptWindow.relevantAttempts
 *                          (the in-scope evidence pool)
 * - `transferTaskIds`    — copy of transferEvidence.taskIdsTried
 * - `delayedReviewAttemptIds` — itemIds from
 *                          delayedReviewEvidence.reviewAttempts
 * - `misconceptionId`    — misconceptionSignal.misconceptionId (the
 *                          dominant misconception in the window, or
 *                          null if none detected)
 */
function buildEvidenceUsed(
  attemptWindow: AttemptWindow,
  transferEvidence: TransferEvidence,
  delayedReviewEvidence: DelayedReviewEvidence,
  misconceptionSignal: MisconceptionSignal,
): AdaptationDecision["evidenceUsed"] {
  return {
    attempts: attemptWindow.relevantAttempts.map((a) => a.itemId),
    transferTaskIds: [...transferEvidence.taskIdsTried],
    delayedReviewAttemptIds: delayedReviewEvidence.reviewAttempts.map(
      (a) => a.itemId,
    ),
    misconceptionId: misconceptionSignal.misconceptionId,
  };
}

/** Finalize a decision: attach parentExplanation (Phase F3) and
 * evidenceUsed (populated by buildEvidenceUsed at the engine entry
 * point and threaded through PartialDecision). */
function finalizeDecision(
  decision: AdaptationDecisionType,
  partial: PartialDecision,
): AdaptationDecision {
  const parentExplanation = buildParentExplanation({
    childId: partial.childId,
    skillId: partial.currentSkillId,
    decision,
    supportLevelUsed: partial.supportModeSummary,
    reasonCodes: partial.reasonCodes,
    blockerCodes: partial.blockerCodes,
    hasSameDayAttempt: partial.hasSameDayAttempt,
    readAloudHeard: partial.readAloudHeard,
    postIdkHelped: partial.postIdkHelped,
    durableMastery: partial.durableMastery,
  });

  return {
    decision,
    childId: partial.childId,
    currentSkillId: partial.currentSkillId,
    nextSkillId: partial.nextSkillId,
    remediationSkillId: partial.remediationSkillId,
    selectedActivityType: partial.selectedActivityType,
    selectedContentFile: partial.selectedContentFile,
    selectedContentId: partial.selectedContentId,
    reasonCodes: partial.reasonCodes,
    blockerCodes: partial.blockerCodes,
    evidenceUsed: partial.evidenceUsed,
    supportModeSummary: partial.supportModeSummary,
    parentExplanation,
    requiredFollowUp: partial.requiredFollowUp,
    debugTrace: partial.debugTrace,
  };
}
